import { Form, Modal, Select } from "antd"
import React, { useState } from "react"

import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { ProjectTypeSelect } from "../../../../../../../components/Shared/ProjectTypeSelect.tsx"
import { useAppSelector } from "../../../../../../../hooks/use-app-selector.ts"
import { type ProjectTypeFormData } from "../../../../../types/project.types.ts"
import { PROJECT_ENTITY_CATEGORIES } from "../../../../constants/project-entity-category.ts"

const AMOUNT_RULES = [
  { required: false },
  {
    type: "number" as const,
    transform: (v: unknown) => (isNaN(+(v as number)) ? 0 : +(v as number)),
    min: 0,
    message: "Must be at least 0"
  }
]

interface ProjectViewAddProjectTypeModalProps {
  open: boolean
  onClose: () => void
  isEdit?: boolean
  /** `false` keeps the form open: the caller asked something and was told no. */
  onSubmit: (data: ProjectTypeFormData) => Promise<boolean | void> | undefined
  initialValues?: ProjectTypeFormData
}

export function ProjectViewAddProjectTypeModal({
  open,
  isEdit,
  initialValues,
  onClose,
  onSubmit
}: ProjectViewAddProjectTypeModalProps) {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm<ProjectTypeFormData>()

  const category = Form.useWatch("category", form)
  const isChangeRequest = category === PROJECT_ENTITY_CATEGORIES.CHANGE_REQUEST

  const categories = useAppSelector(
    (state) => state?.projects?.filterOptions?.category || []
  )

  const handleOk = async () => {
    try {
      setLoading(true)
      const values = await form.validateFields()
      if ((await onSubmit(values)) === false) return
      onClose()
    } catch (e) {
      console.log(`Error occurred: ${e}`)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    onClose()
    form.resetFields()
  }

  return (
    <Modal
      title={isEdit ? "Edit Project Type" : "Add Project Type"}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={isEdit ? "Save" : "Add"}
      cancelText="Cancel"
      destroyOnHidden
      closable={true}
      maskClosable={false}
      keyboard={false}
      cancelButtonProps={{ disabled: loading }}
      okButtonProps={{ loading }}
    >
      <Form<ProjectTypeFormData>
        form={form}
        clearOnDestroy
        layout="vertical"
        validateTrigger={["onSubmit", "onChange"]}
        className="with-default-error"
        disabled={loading}
        initialValues={initialValues}
      >
        {!isEdit && (
          <Form.Item<ProjectTypeFormData>
            label="Project Type"
            name="project_type_id"
            rules={[{ required: true, message: "Please select project type" }]}
          >
            <ProjectTypeSelect mode={null} />
          </Form.Item>
        )}

        <Form.Item<ProjectTypeFormData>
          label="Type"
          name="category"
          rules={[{ required: true, message: "Please select type" }]}
        >
          <Select
            className={"w-full"}
            placeholder={"Select a type"}
            options={categories}
          />
        </Form.Item>

        {isChangeRequest && (
          <>
            <Form.Item<ProjectTypeFormData>
              label="Estimated Receivable"
              name="estimated_receivable"
              rules={AMOUNT_RULES}
            >
              <InputNumber
                min={0}
                max={999999999.99}
                step={0.1}
                precision={2}
                className="w-full"
                prefix="$"
                placeholder="00.00"
              />
            </Form.Item>

            <Form.Item<ProjectTypeFormData>
              label="Estimated Payable"
              name="estimated_payable"
              rules={AMOUNT_RULES}
            >
              <InputNumber
                min={0}
                max={999999999.99}
                step={0.1}
                precision={2}
                className="w-full"
                prefix="$"
                placeholder="00.00"
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}
